import axios, { AxiosError, AxiosInstance, AxiosResponse, InternalAxiosRequestConfig } from "axios";

// 필드 에러 상세 정보 타입 정의
export interface FieldErrorDetail {
  field: string;
  message: string;
  value: string;
}

// API 응답 타입 정의
export interface ApiResponse<T> {
  success: boolean;
  data: T;
  message: string;
  code: string;
  errors: FieldErrorDetail[];
}

// API 에러 타입 정의
export interface ApiError {
  success: boolean;
  message: string;
  code: string;
  errors: FieldErrorDetail[];
}

// 전역 에러 핸들러
let globalErrorHandler: ((message: string) => void) | null = null;

export const setGlobalErrorHandler = (handler: (message: string) => void) => {
  globalErrorHandler = handler;
};

// axios 인스턴스 생성
const axiosInstance: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE || "/api",
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: true,
  // xsrfCookieName: "XSRF-TOKEN",
  // xsrfHeaderName: "X-XSRF-TOKEN",
});

// 요청 인터셉터
axiosInstance.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터
axiosInstance.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data.code === "SUCCESS") {
      return response.data;
    }

    const errorMessage = response.data.message || "서버 오류가 발생했습니다.";
    if (globalErrorHandler) {
      globalErrorHandler(errorMessage);
    }
    return Promise.reject(response);
  },
  (error: AxiosError) => {
    // 요청이 취소된 경우는 무시
    if ((error as any).code === "ERR_CANCELED" || (axios.isCancel && axios.isCancel(error))) {
      return;
    }

    console.log("error ====>", error);
    if (error.response) {
      // 서버에서 응답을 받았지만 상태 코드가 2xx 범위를 벗어난 경우
      const responseData = error.response.data as any;
      const apiError: ApiError = {
        success: false,
        message: responseData?.message || responseData?.error || "서버 오류가 발생했습니다.",
        code: error.response.status.toString(),
        errors: responseData?.errors || [],
      };

      if (globalErrorHandler) {
        globalErrorHandler(apiError.message);
      }

      const status = error.response.status;
      if (status === 401) {
        window.location.href = "/error/401";
        return Promise.reject(apiError);
      }

      if (status === 403) {
        window.location.href = "/error/403";
        return Promise.reject(apiError);
      }

      return Promise.reject(apiError);
    } else if (error.request) {
      // 요청은 보냈지만 응답을 받지 못한 경우
      const errorMessage = "서버에 연결할 수 없습니다.";
      if (globalErrorHandler) {
        globalErrorHandler(errorMessage);
      }
      return Promise.reject({
        success: false,
        message: errorMessage,
        code: "0",
        errors: [],
      });
    } else {
      // 요청 설정 중에 오류가 발생한 경우
      const errorMessage = "요청 설정 중 오류가 발생했습니다.";
      if (globalErrorHandler) {
        globalErrorHandler(errorMessage);
      }
      return Promise.reject({
        success: false,
        message: errorMessage,
        code: "0",
        errors: [],
      });
    }
  }
);

export default axiosInstance;
